'use client';

import { useTranslations } from 'next-intl';
import { Label } from '@/shared/ui/shadcn/label';
import { cn } from '@/shared/lib/cn';
import type {
  BuildingStageField as BuildingStageFieldData,
  BuildingStageValue,
} from '@/types/project';

interface BuildingStageFieldProps {
  stage: BuildingStageFieldData;
  disabled?: boolean;
  onChange: (value: BuildingStageValue) => void;
}

/**
 * Where the building physically is — the one field that decides whether designs
 * are made FROM a photo or reconstructed from drawings.
 *
 * Never a blank question: the server proposes a value from the uploaded photos
 * and marks it `is_estimated`, so the user confirms or corrects rather than
 * filling in a form. Same contract as the facade-area field.
 *
 * Every label and description comes from the API (localized server-side); this
 * component only lays them out.
 */
export function BuildingStageField({
  stage,
  disabled,
  onChange,
}: BuildingStageFieldProps) {
  const t = useTranslations('project');

  const options = stage.options ?? [];

  if (options.length === 0) return null;

  return (
    <div className="space-y-2">
      <Label>{t('buildingStage')}</Label>

      <div className="grid gap-2 sm:grid-cols-3">
        {options.map((option) => {
          const isSelected = option.value === stage.value;

          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              onClick={() => onChange(option.value)}
              aria-pressed={isSelected}
              className={cn(
                'rounded-lg border p-3 text-start transition-colors',
                'disabled:cursor-not-allowed disabled:opacity-60',
                isSelected
                  ? 'border-primary bg-primary/5'
                  : 'border-border hover:border-primary/40'
              )}
            >
              <span className="text-form-label block font-medium">
                {option.label}
              </span>
              <span className="text-form-help text-muted-foreground mt-1 block">
                {option.description}
              </span>
            </button>
          );
        })}
      </div>

      {stage.value !== null && stage.is_estimated && (
        <p className="text-form-help text-muted-foreground">
          {t('buildingStageEstimated')}
        </p>
      )}
    </div>
  );
}
